import { getJson } from './api';
import { formatMoney, formatMoneyCompact } from './cash-flow';
import { STATS_INTERVAL, type StatsInterval } from './leads';
import { periodLabel } from './report-window';

/**
 * Lead cohorts (Analytics → Leads): typed fetcher over /api/v3/leads/cohorts
 * (view_analytics) and the pure helpers that turn cohorts into heatmap cells
 * and table rows. Shapes mirror the API's lead-cohorts.ts.
 */

export interface CohortPeriod {
  readonly booked: number;
  readonly lost: number;
  readonly bookedValue: number;
}

export interface LeadCohort {
  readonly key: string;
  readonly startDay: string;
  readonly leads: number;
  readonly booked: number;
  readonly lost: number;
  readonly open: number;
  readonly noDeal: number;
  readonly bookedValue: number;
  readonly medianDaysToBook: number | null;
  /** Cumulative standing at the end of each period since the cohort arrived, period 0 first. */
  readonly periods: readonly CohortPeriod[];
}

export interface LeadCohorts {
  readonly interval: StatsInterval;
  /** Oldest first. */
  readonly cohorts: readonly LeadCohort[];
  readonly periodCount: number;
  readonly asOfDay: string;
  /** The range held more cohorts than the interval can show; only the newest came back. */
  readonly capped: boolean;
}

export const LEAD_COHORTS_QUERY_KEY = {
  root: ['lead-cohorts'] as const,
  window: (interval: StatsInterval, from: string) => ['lead-cohorts', interval, from] as const,
};

/** Cohorts by `interval` from the one holding `from` (YYYY-MM-DD, the page's range start) through today. */
export function readLeadCohorts(interval: StatsInterval, from: string, signal?: AbortSignal): Promise<LeadCohorts> {
  return getJson<LeadCohorts>('/leads/cohorts', { interval, from }, signal);
}

/** What the heatmap colors by. */
export const COHORT_METRIC = {
  bookRate: 'bookRate',
  lostRate: 'lostRate',
  bookedValue: 'bookedValue',
  valuePerLead: 'valuePerLead',
} as const;
export type CohortMetric = (typeof COHORT_METRIC)[keyof typeof COHORT_METRIC];

/**
 * Rates go to the heatmap in percent points (13, not 0.13): its color scale
 * starts at 0–1, so shares would all sit in its palest tenth.
 */
const PERCENT = 100;
const formatPoints = (points: number): string => `${Math.round(points)}%`;

/**
 * How a cohort's row reads. `eachPeriod`: what happened in that period alone,
 * so a column shows when bookings land (for planning: leads in times the
 * typical share per period). `runningTotal`: the standing by the end of each
 * period, so a row only grows (for comparing cohorts at the same age).
 */
export const COHORT_VIEW = { eachPeriod: 'eachPeriod', runningTotal: 'runningTotal' } as const;
export type CohortView = (typeof COHORT_VIEW)[keyof typeof COHORT_VIEW];

/** The toggle's labels, by interval: "Each week" / "Running total". */
export function cohortViewLabel(view: CohortView, interval: StatsInterval): string {
  return view === COHORT_VIEW.eachPeriod ? `Each ${COHORT_INTERVAL_NOUN[interval]}` : 'Running total';
}

/** How the Typical row averages a metric across cohorts. */
const TYPICAL = {
  /** Pooled: the cohorts' counts summed over their leads summed, so big cohorts weigh more. */
  pooled: 'pooled',
  /** Mean of the cohorts' values (dollars booked, which has no per-lead base). */
  mean: 'mean',
} as const;

interface MetricSpec {
  readonly label: string;
  /** The heatmap's unit, read in the tooltip and by screen readers. */
  readonly unit: string;
  readonly format: (value: number) => string;
  /** The cell value for a cohort of `leads` standing at `period`. */
  readonly value: (period: CohortPeriod, leads: number) => number;
  readonly typical: (typeof TYPICAL)[keyof typeof TYPICAL];
}

export const COHORT_METRIC_SPEC: Readonly<Record<CohortMetric, MetricSpec>> = {
  [COHORT_METRIC.bookRate]: {
    label: 'Booked %',
    typical: TYPICAL.pooled,
    unit: 'of leads booked',
    format: formatPoints,
    value: (period, leads) => (PERCENT * period.booked) / leads,
  },
  [COHORT_METRIC.lostRate]: {
    label: 'Lost %',
    typical: TYPICAL.pooled,
    unit: 'of leads lost',
    format: formatPoints,
    value: (period, leads) => (PERCENT * period.lost) / leads,
  },
  [COHORT_METRIC.bookedValue]: {
    label: 'Booked $',
    typical: TYPICAL.mean,
    unit: 'booked',
    format: formatMoneyCompact,
    value: (period) => period.bookedValue,
  },
  [COHORT_METRIC.valuePerLead]: {
    label: '$ per lead',
    typical: TYPICAL.pooled,
    unit: 'booked per lead',
    format: (value) => formatMoney(Math.round(value)),
    value: (period, leads) => period.bookedValue / leads,
  },
};

/** The bucket name in running text: "the week they arrived". */
export const COHORT_INTERVAL_NOUN: Readonly<Record<StatsInterval, string>> = {
  [STATS_INTERVAL.day]: 'day',
  [STATS_INTERVAL.week]: 'week',
  [STATS_INTERVAL.month]: 'month',
};

const PERIOD_PREFIX: Readonly<Record<StatsInterval, string>> = {
  [STATS_INTERVAL.day]: 'Day',
  [STATS_INTERVAL.week]: 'Wk',
  [STATS_INTERVAL.month]: 'Mo',
};

/** Column labels: "Wk 0" is the week the leads arrived, "Wk 1" the next. */
export function cohortColumns(cohorts: LeadCohorts): string[] {
  return Array.from({ length: cohorts.periodCount }, (_, index) => `${PERIOD_PREFIX[cohorts.interval]} ${index}`);
}

/**
 * Row labels: "Oct 12 · 41" (arrival and lead count). Day and week labels
 * carry no year, so once the cohorts span more than one year they get one:
 * "Oct 12 ’25 · 41".
 */
export function cohortRowLabels(cohorts: LeadCohorts): string[] {
  const years = new Set(cohorts.cohorts.map((cohort) => cohort.key.slice(0, 4)));
  const withYear = cohorts.interval !== STATS_INTERVAL.month && years.size > 1;
  return cohorts.cohorts.map((cohort) => {
    const label = periodLabel(cohort.key, cohorts.interval);
    const dated = withYear ? `${label} ’${cohort.key.slice(2, 4)}` : label;
    return `${dated} · ${cohort.leads.toLocaleString('en-US')}`;
  });
}

const ZERO_PERIOD: CohortPeriod = { booked: 0, lost: 0, bookedValue: 0 };

/** What the cohort did in period `index` alone: its standing then, less its standing a period earlier. */
function periodDelta(cohort: LeadCohort, index: number): CohortPeriod | undefined {
  const period = cohort.periods[index];
  if (!period) return undefined;
  const before = index > 0 ? (cohort.periods[index - 1] ?? ZERO_PERIOD) : ZERO_PERIOD;
  return { booked: period.booked - before.booked, lost: period.lost - before.lost, bookedValue: period.bookedValue - before.bookedValue };
}

function periodFor(cohort: LeadCohort, index: number, view: CohortView): CohortPeriod | undefined {
  return view === COHORT_VIEW.eachPeriod ? periodDelta(cohort, index) : cohort.periods[index];
}

/**
 * The heatmap cells: one row per cohort, one column per period since it
 * arrived. A period that has not begun yet, or a cohort with no leads, is
 * null (drawn blank) so the young cohorts read as unfinished, not as zero.
 */
export function cohortMatrix(
  cohorts: LeadCohorts,
  metric: CohortMetric,
  view: CohortView = COHORT_VIEW.runningTotal,
): Array<Array<number | null>> {
  const spec = COHORT_METRIC_SPEC[metric];
  return cohorts.cohorts.map((cohort) =>
    Array.from({ length: cohorts.periodCount }, (_, index) => {
      const period = periodFor(cohort, index, view);
      return period && cohort.leads > 0 ? spec.value(period, cohort.leads) : null;
    }),
  );
}

/**
 * The Typical row: for each period since arrival, the average across the
 * cohorts old enough to have reached it. Rates and dollars per lead pool the
 * cohorts (summed counts over summed leads); dollars booked is the mean per
 * cohort. A period no cohort has reached is null.
 */
export function cohortTypicalRow(
  cohorts: LeadCohorts,
  metric: CohortMetric,
  view: CohortView = COHORT_VIEW.runningTotal,
): Array<number | null> {
  const spec = COHORT_METRIC_SPEC[metric];
  return Array.from({ length: cohorts.periodCount }, (_, index) => {
    let leads = 0;
    let reached = 0;
    const sum = { ...ZERO_PERIOD };
    for (const cohort of cohorts.cohorts) {
      const period = periodFor(cohort, index, view);
      if (!period || cohort.leads === 0) continue;
      reached += 1;
      leads += cohort.leads;
      sum.booked += period.booked;
      sum.lost += period.lost;
      sum.bookedValue += period.bookedValue;
    }
    if (reached === 0) return null;
    if (spec.typical === TYPICAL.mean) return sum.bookedValue / reached;
    return spec.value(sum, leads);
  });
}

/** A cohort flattened for the sortable table. Rates are null for an empty cohort. */
export interface CohortTableRow {
  readonly key: string;
  readonly leads: number;
  readonly booked: number;
  readonly bookRate: number | null;
  readonly lostRate: number | null;
  readonly open: number;
  readonly bookedValue: number;
  readonly valuePerLead: number | null;
  readonly medianDaysToBook: number | null;
}

export function cohortTableRows(cohorts: LeadCohorts): CohortTableRow[] {
  return cohorts.cohorts.map((cohort) => {
    const share = (count: number): number | null => (cohort.leads > 0 ? count / cohort.leads : null);
    return {
      key: cohort.key,
      leads: cohort.leads,
      booked: cohort.booked,
      bookRate: share(cohort.booked),
      lostRate: share(cohort.lost),
      open: cohort.open,
      bookedValue: cohort.bookedValue,
      valuePerLead: cohort.leads > 0 ? cohort.bookedValue / cohort.leads : null,
      medianDaysToBook: cohort.medianDaysToBook,
    };
  });
}
